/**
 * A roleta do sorteio de encontros — o `randomizer_card.dart` com o
 * `celebration_dialog.dart` dentro.
 *
 * Gira dois segundos, escolhe uma ideia e abre a festa com o título. O botão
 * de cima troca entre o sorteio do arquivo e a ideia inédita da IA; quando a
 * IA não responde, a ideia do arquivo entra no lugar sem avisar, como lá.
 *
 * O que o navegador obrigou a mudar:
 *
 * - O `SweepGradient` do aro vira um `conic-gradient` começando às três horas,
 *   que é onde o ângulo zero do Flutter fica.
 * - As dezesseis marcas do `_RoulettePainter` viram dezesseis traços girados no
 *   CSS: um `<canvas>` que nunca muda desenharia o mesmo por muito mais.
 * - O `elasticOut` da festa não cabe numa curva do CSS, que só sabe uma
 *   ondulação. São quatro paradas de `@keyframes` fazendo o mesmo balanço.
 * - As quinze faíscas dependem do `Random(index)` do Dart, cuja sequência não
 *   se reproduz aqui. A conta é outra, a ideia é a mesma: o índice entra, a
 *   roda de faíscas sai sempre igual.
 *
 * E uma escolha que o navegador não obrigou, mas que a tela pediu: lá a roleta
 * para depois dos dois segundos e só então a IA é consultada, com o aviso de
 * "decidindo" parado na tela. Aqui a espera e o pedido correm juntos, então a
 * roleta gira até a resposta chegar.
 */

import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { idealizarEncontro, traduzir, usarSessao } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import { sorteio } from '../nucleo/sorteio';
import './cartaoDeSorteio.css';

/** O `Future.delayed(2000)`: a roleta gira ao menos isso, sempre. */
const ESPERA = 2000;

/** As dezesseis marcas do aro, de 22,5 em 22,5 graus. */
const MARCAS = 16;

/** As quinze faíscas da festa. */
const FAISCAS = 15;

export function CartaoDeSorteio() {
  const { casa } = usarSessao();
  const [girando, setGirando] = useState(false);
  const [ia, setIa] = useState(false);
  const [festa, setFesta] = useState<string | null>(null);
  const ultima = useRef<string | undefined>(undefined);
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    // As ideias são catorze kB que só interessam a quem chega até aqui, e
    // pedi-las agora é o que faz a espera de dois segundos cobrir a leitura.
    void sorteio().catch(() => {});
    return () => {
      vivo.current = false;
    };
  }, []);

  async function girar() {
    if (girando) return;
    setGirando(true);

    const [inedita, banco] = await Promise.all([
      ia ? idealizarEncontro() : Promise.resolve(null),
      sorteio().catch(() => null),
      new Promise((pronto) => setTimeout(pronto, ESPERA)),
    ]);

    if (!vivo.current) return;
    setGirando(false);

    // Repetir a mesma ideia duas vezes seguidas parece botão quebrado; lá isso
    // não acontece porque o arquivo tem cento e dezoito.
    const escolhida = inedita ?? banco?.sortear(casa?.formato, ultima.current) ?? null;
    if (!escolhida) return;
    ultima.current = escolhida.id;
    setFesta(escolhida.titulo);
  }

  return (
    <div className={girando ? 'sr girando' : 'sr'}>
      <div className="sr-corpo">
        <span className="sr-fio" aria-hidden="true" />

        <h2 className="sr-nome">{traduzir('Digital Roulette')}</h2>
        <p className="sr-recado">{traduzir('GIRE PARA SORTEAR O ENCONTRO')}</p>

        <button
          className={ia ? 'sr-modo ligado' : 'sr-modo'}
          type="button"
          aria-pressed={ia}
          aria-label={
            ia
              ? traduzir('Modo IA ligado. Toque para voltar ao sorteio.')
              : traduzir('Modo sorteio. Toque para ligar a IA.')
          }
          onClick={() => setIa(!ia)}
        >
          <Icone nome={ia ? 'brilho' : 'embaralhar'} tamanho={14} />
          {ia ? traduzir('AI Powered') : traduzir('Random')}
        </button>

        <div className="sr-roleta">
          <span className="sr-aro" aria-hidden="true">
            <span className="sr-miolo">
              {Array.from({ length: MARCAS }, (_, i) => (
                <i key={i} style={{ ['--sr-giro' as string]: `${i * 22.5}deg` }} />
              ))}
              <span className="sr-anel" />
            </span>
          </span>
          <button
            className="sr-coracao"
            type="button"
            onClick={() => void girar()}
            aria-label={traduzir('Sortear um encontro')}
          >
            <Icone nome="coracao" tamanho={62} preenchido />
          </button>
        </div>

        <p className="sr-estado" aria-live="polite">
          {girando ? traduzir('DECIDINDO O DESTINO...') : traduzir('TOQUE NO CORAÇÃO')}
        </p>
      </div>

      <Festa titulo={festa} aoFechar={() => setFesta(null)} />
    </div>
  );
}

/**
 * O `CelebrationDialog`.
 *
 * Não é uma `Folha` da casa porque as faíscas saem do cartão e vão a duzentos
 * pixels do centro: a folha as cortaria na borda, e o escuro dela é escuro, e
 * não o rosa que este abre por trás.
 */
function Festa({ titulo, aoFechar }: { titulo: string | null; aoFechar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const festa = ref.current;
    if (!festa) return;
    if (titulo !== null && !festa.open) festa.showModal();
    else if (titulo === null && festa.open) festa.close();
  }, [titulo]);

  function noFundo(e: MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) aoFechar();
  }

  return (
    <dialog
      ref={ref}
      className="festa"
      aria-label={titulo ?? ''}
      onClick={noFundo}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
    >
      {titulo !== null ? (
        <div className="festa-corpo">
          <span className="festa-faiscas" aria-hidden="true">
            {Array.from({ length: FAISCAS }, (_, i) => {
              const angulo = (i / FAISCAS) * 2 * Math.PI;
              const distancia = 160 + fixo(i, 0) * 60;
              const tamanho = Math.round(12 + fixo(i, 1) * 24);
              const cor = i % 3 === 0 ? 'rosa' : i % 3 === 1 ? 'ouro' : 'lavanda';
              return (
                <i
                  key={i}
                  className={i % 2 === 0 ? cor : `${cor} bola`}
                  style={{
                    ['--fx' as string]: `${(Math.cos(angulo) * distancia).toFixed(1)}px`,
                    ['--fy' as string]: `${(Math.sin(angulo) * distancia).toFixed(1)}px`,
                    ['--ftam' as string]: `${tamanho}px`,
                    ['--fatraso' as string]: `${i * 50}ms`,
                  }}
                >
                  {i % 2 === 0 ? <Icone nome="estrela" tamanho={tamanho} preenchido /> : null}
                </i>
              );
            })}
          </span>

          <div className="festa-cartao">
            <span className="festa-coracao" aria-hidden="true">
              <Icone nome="coracao" tamanho={48} preenchido />
            </span>
            <p className="festa-convite">{traduzir('You should...')}</p>
            <h2 className="festa-ideia">{titulo}</h2>
            <button className="festa-ok" type="button" onClick={aoFechar}>
              {traduzir('Perfeito!')}
            </button>
          </div>
        </div>
      ) : null}
    </dialog>
  );
}

/**
 * O `Random(index)` do Dart, que semeia cada faísca para que ela caia sempre no
 * mesmo lugar. A sequência dele não se reproduz aqui; esta conta faz o mesmo
 * serviço — o índice entra, um número entre zero e um sai, e sai igual sempre.
 */
function fixo(semente: number, volta: number): number {
  const x = Math.sin(semente * 127.1 + volta * 311.7) * 43758.5453;
  return x - Math.floor(x);
}
